import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ADSENSE_CLIENT, AD_SLOTS } from "../config/adsense";

const isProduction = process.env.NODE_ENV === "production";

/**
 * A single AdSense display unit.
 *
 * `name` is a key from AD_SLOTS rather than a raw slot id, so placements are
 * named in the page and the ids live in one file.
 */
function AdUnit({ name, className = "", format = "auto", layout }) {
    const slot = AD_SLOTS[name];
    const insRef = useRef(null);
    const { pathname } = useLocation();

    useEffect(() => {
        if (!isProduction || !slot) return undefined;

        const ins = insRef.current;
        if (!ins) return undefined;

        let cancelled = false;

        const push = () => {
            const el = insRef.current;
            if (cancelled || !el) return false;

            // adsbygoogle stamps this attribute on an <ins> it has claimed and
            // throws "already have ads in them" if the same one is pushed
            // twice — which StrictMode's double-invoked effect would do.
            if (el.getAttribute("data-adsbygoogle-status")) return true;

            // A zero-width slot makes AdSense bail with "availableWidth=0" and
            // it never retries, so wait for the parent to finish laying out.
            if (el.offsetWidth === 0) return false;

            try {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            } catch (error) {
                console.error(`[AdUnit] failed to request "${name}"`, error);
            }
            return true;
        };

        if (push()) return undefined;

        const observer = new ResizeObserver(() => {
            if (push()) observer.disconnect();
        });
        observer.observe(ins);

        return () => {
            cancelled = true;
            observer.disconnect();
        };
        // pathname is a dependency so a client-side navigation that reuses this
        // component instance (e.g. /product/a → /product/b) requests a new ad.
    }, [slot, pathname, name]);

    if (!slot) {
        if (isProduction) return null;
        return (
            <div
                className={`my-10 rounded-xl border border-dashed border-sand bg-cream/60 px-4 py-8 text-center text-xs uppercase tracking-widest text-muted ${className}`}
            >
                Ad slot “{name}” — add its id to src/config/adsense.js
            </div>
        );
    }

    return (
        <div className={`my-10 ${className}`}>
            <ins
                // Remounting on navigation gives adsbygoogle a clean, unclaimed
                // element; reusing the filled one would be a no-op.
                key={`${name}-${pathname}`}
                ref={insRef}
                className="adsbygoogle"
                style={{ display: "block" }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={slot}
                data-ad-format={format}
                data-ad-layout={layout}
                data-full-width-responsive="true"
            />
        </div>
    );
}

export default AdUnit;
